import React, { useState, useEffect } from 'react';
import { X, Bus, Save, Loader2 } from 'lucide-react';
import Swal from 'sweetalert2';

const VehicleModal = ({ isOpen, onClose, onSave, vehicle }) => {
  const [formData, setFormData] = useState({
    registrationNumber: '',
    model: '',
    brand: '',
    totalSeats: 48,
    year: new Date().getFullYear(),
    color: '',
    busType: '',
    description: '',
    isAvailable: true,
    driverId: '',
    contactNo: '',
    seatLayout: '4x12',
    notAvailableSeat: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');

  const isEditing = !!vehicle;

  useEffect(() => {
    if (vehicle) {
      setFormData({
        registrationNumber: vehicle.registrationNumber || '',
        model: vehicle.model || '',
        brand: vehicle.brand || '',
        totalSeats: vehicle.totalSeats || 48,
        year: vehicle.year || new Date().getFullYear(),
        color: vehicle.color || '',
        busType: vehicle.busType || '',
        description: vehicle.description || '',
        isAvailable: vehicle.isAvailable !== undefined ? vehicle.isAvailable : true,
        driverId: vehicle.driverId || '',
        contactNo: vehicle.contactNo || '',
        seatLayout: vehicle.seatLayout || '4x12',
        // If NotAvailableSeat is "0", show empty string in the form (better UX)
        notAvailableSeat: (vehicle.notAvailableSeat && vehicle.notAvailableSeat !== '0') 
          ? vehicle.notAvailableSeat 
          : ((vehicle.notAvailableSeats && vehicle.notAvailableSeats !== '0') ? vehicle.notAvailableSeats : '')
      });
    } else {
      setFormData({
        registrationNumber: '',
        model: '',
        brand: '',
        totalSeats: 48,
        year: new Date().getFullYear(),
        color: '',
        busType: '',
        description: '',
        isAvailable: true,
        driverId: '',
        contactNo: '',
        seatLayout: '4x12',
        notAvailableSeat: ''
      });
    }
    setErrors({});
  }, [vehicle]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // Auto-update totalSeats when seatLayout changes
      if (name === 'seatLayout') {
        if (value === '51 Layout') {
          updated.totalSeats = 51;
        } else {
          // Parse standard layouts like "4x12" to calculate seats
          const layoutMatch = value.match(/(\d+)[x×](\d+)/i);
          if (layoutMatch) {
            const columns = parseInt(layoutMatch[1]);
            const rows = parseInt(layoutMatch[2]);
            updated.totalSeats = columns * rows;
          }
        }
      }
      
      return updated;
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.registrationNumber.trim()) {
      newErrors.registrationNumber = 'Registration number is required';
    }

    if (!formData.model.trim()) {
      newErrors.model = 'Model is required';
    }

    if (!formData.brand.trim()) {
      newErrors.brand = 'Brand is required';
    }

    if (formData.totalSeats < 1 || formData.totalSeats > 100) {
      newErrors.totalSeats = 'Total seats must be between 1 and 100';
    }

    if (formData.year < 1900 || formData.year > new Date().getFullYear() + 1) {
      newErrors.year = 'Year must be between 1900 and next year';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setApiError('');
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving vehicle:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack
      });
      
      // Extract error message - the error.message should already contain the detailed message from API
      let errorMessage = error.message || 'Failed to save vehicle. Please try again.';
      
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Bus className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white">
                    {isEditing ? 'Edit Vehicle' : 'Add New Vehicle'}
                  </h3>
                  <p className="text-blue-100">
                    {isEditing ? 'Update vehicle information' : 'Enter vehicle details'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                className="text-white hover:text-gray-200 transition-colors disabled:opacity-50"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {/* API Error Message */}
            {apiError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                <strong>Error:</strong> {apiError}
                <button 
                  onClick={() => setApiError('')}
                  className="float-right font-bold text-red-700 hover:text-red-900"
                >
                  ×
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Registration Number & Model */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registration Number *
                  </label>
                  <input
                    type="text"
                    name="registrationNumber"
                    value={formData.registrationNumber}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.registrationNumber ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="e.g., NC 6055"
                    disabled={loading}
                  />
                  {errors.registrationNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.registrationNumber}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Model *
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.model ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Super Luxury"
                    disabled={loading}
                  />
                  {errors.model && (
                    <p className="mt-1 text-sm text-red-600">{errors.model}</p>
                  )}
                </div>
              </div>

              {/* Brand & Year */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Brand *
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.brand ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Volvo"
                    disabled={loading}
                  />
                  {errors.brand && (
                    <p className="mt-1 text-sm text-red-600">{errors.brand}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Year *
                  </label>
                  <input
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.year ? 'border-red-300' : 'border-gray-300'
                    }`}
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    disabled={loading}
                  />
                  {errors.year && (
                    <p className="mt-1 text-sm text-red-600">{errors.year}</p>
                  )}
                </div>
              </div>

              {/* Total Seats & Color */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Seats *
                  </label>
                  <input
                    type="number"
                    name="totalSeats"
                    value={formData.totalSeats}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.totalSeats ? 'border-red-300' : 'border-gray-300'
                    }`}
                    min="1"
                    max="100"
                    disabled={loading}
                  />
                  {errors.totalSeats && (
                    <p className="mt-1 text-sm text-red-600">{errors.totalSeats}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color
                  </label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., White"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Bus Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bus Type
                </label>
                <select
                  name="busType"
                  value={formData.busType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                >
                  <option value="">Select Bus Type</option>
                  <option value="AC">AC</option>
                  <option value="Non-AC">Non-AC</option>
                  <option value="Super Luxury">Super Luxury</option>
                  <option value="Semi-Luxury">Semi-Luxury</option>
                  <option value="Express">Express</option>
                </select>
              </div>

              {/* Seat Layout & Driver */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Seat Layout
                  </label>
                  <select
                    name="seatLayout"
                    value={formData.seatLayout}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading}
                  >
                    <option value="4x12">4 columns × 12 rows (48 seats)</option>
                    <option value="4x11">4 columns × 11 rows (44 seats)</option>
                    <option value="4x10">4 columns × 10 rows (40 seats)</option>
                    <option value="3x12">3 columns × 12 rows (36 seats)</option>
                    <option value="3x10">3 columns × 10 rows (30 seats)</option>
                    <option value="51 Layout">51 Layout (51 seats)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Driver ID
                  </label>
                  <input
                    type="text"
                    name="driverId"
                    value={formData.driverId}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., DR001"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Not Available Seats */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Not Available Seats
                </label>
                <input
                  type="text"
                  name="notAvailableSeat"
                  value={formData.notAvailableSeat}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 1,5,11 (comma-separated seat numbers)"
                  disabled={loading}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter seat numbers that are not available for booking (comma-separated, e.g., 1,5,11)
                </p>
              </div>

              {/* Contact Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Number
                </label>
                <input
                  type="tel"
                  name="contactNo"
                  value={formData.contactNo}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., +94 71 123 4567"
                  disabled={loading}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter vehicle description..."
                  disabled={loading}
                />
              </div>

              {/* Availability */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isAvailable"
                  name="isAvailable"
                  checked={formData.isAvailable}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  disabled={loading}
                />
                <label htmlFor="isAvailable" className="text-sm font-medium text-gray-700">
                  Vehicle is available for service
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 bg-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-400 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:bg-blue-400 flex items-center justify-center space-x-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      <span>{isEditing ? 'Update Vehicle' : 'Add Vehicle'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleModal;
